import React, { useEffect, useState } from "react";
import { Save, Loader2, PlayCircle, AlertTriangle, CheckCircle2 } from "lucide-react";
import { Panel, MonoLabel } from "@/components/Panel";
import { toast } from "sonner";
import axios from "axios";
import { api, errText, fieldErrors, TOKEN_KEY } from "@/lib/devadminApi";

const INT_FIELDS = [
  ["rounds_count", "Rounds", "How many conclave rounds run this day", 1, 20],
  ["round_duration_min", "Round duration (min)", "Minutes per round", 1, 240],
  ["table_size", "Table size", "Seats per round table", 2, 30],
  ["max_same_city_per_table", "Max same-city / table", "Soft cap — spreads cities across tables", 1, 30],
  ["max_same_chapter_per_table", "Max same-chapter / table", "Soft cap — mixes chapters across tables", 1, 30],
];

const adminApi = axios.create({ baseURL: `${process.env.REACT_APP_BACKEND_URL}/api/admin` });
adminApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem(TOKEN_KEY);
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

export function ConclaveRules({ eventId }) {
  const [conclave, setConclave] = useState(null);
  const [errs, setErrs] = useState({});
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [seed, setSeed] = useState(42);

  useEffect(() => {
    setConclave(null); setPreview(null); setErrs({});
    api.get(`/events/${eventId}/config`).then((r) => setConclave(r.data.conclave || {})).catch((e) => toast.error(errText(e)));
  }, [eventId]);

  const save = async () => {
    setBusy(true); setErrs({});
    const patch = {
      conclave: {
        ...conclave,
        rounds_count: Number(conclave.rounds_count),
        round_duration_min: Number(conclave.round_duration_min),
        table_size: Number(conclave.table_size),
        max_same_city_per_table: Number(conclave.max_same_city_per_table),
        max_same_chapter_per_table: Number(conclave.max_same_chapter_per_table),
        category_fit_weight: Number(conclave.category_fit_weight),
        allow_same_category_when_forced: !!conclave.allow_same_category_when_forced,
      },
    };
    try {
      const { data } = await api.patch(`/events/${eventId}/config`, patch);
      setConclave(data.conclave || {});
      toast.success("Conclave rules saved · audit logged");
    } catch (e) { setErrs(fieldErrors(e)); toast.error(errText(e)); } finally { setBusy(false); }
  };

  const runPreview = async () => {
    setPreviewing(true); setPreview(null);
    try {
      const { data } = await adminApi.get(`/conclave/preview`, { params: { event_id: eventId, seed } });
      setPreview(data);
      if (!data.ok) toast.error(`Preview: ${data.error || "algorithm failed"}`);
      else toast.success(`Preview: ${data.stats.rounds_generated} rounds · ${data.stats.members_placed} placements · ${data.stats.seconds_taken}s`);
    } catch (e) { toast.error(errText(e)); } finally { setPreviewing(false); }
  };

  if (!conclave) return <div className="mono-label" data-testid="conclave-rules-loading">Loading conclave rules…</div>;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <Panel className="p-6 lg:col-span-2" data-testid="conclave-rules-panel">
        <MonoLabel>Conclave Rules</MonoLabel>
        <div className="mono-label mt-2" style={{ color: "var(--taupe)" }}>
          Zero hardcoded constants — the matching engine reads every rule from this panel at runtime.
        </div>
        <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-5">
          {INT_FIELDS.map(([k, label, hint, lo, hi]) => (
            <div key={k}>
              <MonoLabel>{label}</MonoLabel>
              <input
                data-testid={`conclave-rule-${k}`}
                className="input-x mt-2 !font-mono !w-32"
                value={conclave[k] ?? ""}
                onChange={(e) => setConclave({ ...conclave, [k]: e.target.value.replace(/\D/g, "") })}
              />
              <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>{hint} · {lo}–{hi}</div>
            </div>
          ))}
          <div>
            <MonoLabel>Category-fit weight</MonoLabel>
            <input
              data-testid="conclave-rule-category_fit_weight"
              className="input-x mt-2 !font-mono !w-32"
              value={conclave.category_fit_weight ?? ""}
              onChange={(e) => setConclave({ ...conclave, category_fit_weight: e.target.value.replace(/[^0-9.]/g, "") })}
            />
            <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>Multiplier on ranked-category weights · 0–10</div>
          </div>
          <div>
            <MonoLabel>Same-category override</MonoLabel>
            <div className="mt-2 inline-flex hairline">
              {[[true, "Allow when forced"], [false, "Never"]].map(([v, l]) => (
                <button
                  key={String(v)}
                  data-testid={`conclave-rule-force-${v}`}
                  onClick={() => setConclave({ ...conclave, allow_same_category_when_forced: v })}
                  className={`px-4 py-2 text-xs mono-label ${!!conclave.allow_same_category_when_forced === v ? "bg-[var(--red)] text-[var(--text)]" : "text-[var(--taupe)]"}`}
                >{l}</button>
              ))}
            </div>
            <div className="mono-label mt-1" style={{ color: "var(--taupe)" }}>Relaxes rule 1 only if no other table works (rule 2 is never relaxed)</div>
          </div>
        </div>
        {errs.conclave && <div className="text-xs mt-3" data-testid="conclave-rules-error" style={{ color: "var(--red-2)" }}>{errs.conclave}</div>}
        <button data-testid="conclave-rules-save-btn" className="btn-red mt-6" disabled={busy} onClick={save}>
          {busy ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} Save conclave rules
        </button>
      </Panel>

      <Panel className="p-6" data-testid="conclave-preview-panel">
        <MonoLabel>Dry-run preview</MonoLabel>
        <div className="mono-label mt-2" style={{ color: "var(--taupe)" }}>
          Runs the matcher against currently-paid registrations. Nothing is persisted.
        </div>
        <div className="mt-4 flex items-end gap-3">
          <div>
            <MonoLabel>Seed</MonoLabel>
            <input
              data-testid="conclave-preview-seed"
              className="input-x mt-2 !font-mono !w-24"
              value={seed}
              onChange={(e) => setSeed(Number(e.target.value.replace(/\D/g, "")) || 0)}
            />
          </div>
          <button data-testid="conclave-preview-btn" className="btn-ghost" onClick={runPreview} disabled={previewing}>
            {previewing ? <Loader2 size={14} className="animate-spin" /> : <PlayCircle size={14} />} Run preview
          </button>
        </div>
        {preview && (
          <div className="mt-5 hairline p-4 text-sm" data-testid="conclave-preview-result">
            <div className="flex items-center gap-2 mb-3">
              {preview.ok
                ? <CheckCircle2 size={16} style={{ color: "var(--green, #7ac47a)" }} />
                : <AlertTriangle size={16} style={{ color: "var(--red-2)" }} />}
              <span className="mono-label">{preview.ok ? "OK" : "FAIL"} · {preview.members_total} members</span>
            </div>
            {!preview.ok && <div className="mb-3" style={{ color: "var(--red-2)" }}>{preview.error}</div>}
            <ul className="space-y-1 mono-label" style={{ color: "var(--taupe)" }}>
              <li>Rounds generated: <span style={{ color: "var(--text)" }}>{preview.stats.rounds_generated}</span></li>
              <li>Tables/round: <span style={{ color: "var(--text)" }}>{preview.stats.tables_per_round}</span></li>
              <li>Members placed: <span style={{ color: "var(--text)" }}>{preview.stats.members_placed}</span></li>
              <li>Category conflicts relaxed: <span style={{ color: "var(--text)" }}>{preview.stats.category_conflicts_relaxed}</span></li>
              <li>Repeat conflicts: <span style={{ color: "var(--text)" }}>{preview.stats.repeat_conflicts}</span></li>
              <li>Avg category-fit score: <span style={{ color: "var(--text)" }}>{preview.stats.avg_category_fit_score}</span></li>
              <li>Elapsed: <span style={{ color: "var(--text)" }}>{preview.stats.seconds_taken}s</span></li>
            </ul>
            {preview.sample_round?.length > 0 && (
              <div className="mt-4">
                <MonoLabel>Sample · Round 1</MonoLabel>
                <div className="mt-2 space-y-2">
                  {preview.sample_round.map((t) => (
                    <div key={t.table_index} className="text-xs">
                      <span className="mono-label" style={{ color: "var(--taupe)" }}>Table {t.table_index + 1}:</span>{" "}
                      {t.members.map((m) => m.name).join(", ")}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Panel>
    </div>
  );
}
